import { defineStore } from 'pinia';
import { Store } from '@/setup/globals';
import { setInitialData } from '@/stores/plugins/initial-data';
import useSessionStore from '@/stores/session';
import api from '@/helpers/api';

export type StoreState = {
  favourites: number[];
  entries: Record<string, unknown>[];
  pagination: {
    totalAmount: number;
    currentPage: number;
    itemsPerPage: number;
  };
  runningRequests: {
    apiFetchEntries: boolean;
    apiUpdateFavourite: boolean;
  };
};

const storeName = Store.CourseFavourites;

export default defineStore(storeName, {
  state: () => {
    const initialData: Partial<StoreState> = window.initialData?.[storeName] || {};
    const state: StoreState = {
      favourites: [],
      entries: [],
      pagination: {
        totalAmount: 0,
        currentPage: 1,
        itemsPerPage: 6,
      },
      runningRequests: {
        apiFetchEntries: false,
        apiUpdateFavourite: false,
      },
    };

    if (initialData?.entries?.length) {
      initialData.entries = initialData.entries.map((entry) => ({
        ...entry,
        page: initialData?.pagination?.currentPage || 1,
      }));
    }

    setInitialData(state, initialData);

    return state;
  },
  actions: {
    /**
     * Fetches favourite course objects from the server.
     */
    apiFetchEntries({ page, limit, reset = false }) {
      this.runningRequests.apiFetchEntries = true;

      const url = api.getUrl('courses-favorites');

      return api
        .post(url, {}, { params: { page, limit } })
        .then((response) => {
          const { pagination, entries } = response.data?.data || {};

          if (reset) {
            this.entries = [];
          }

          if (Number.isInteger(pagination?.totalAmount)) {
            this.pagination.totalAmount = pagination.totalAmount;
          }

          if (pagination?.currentPage) {
            this.pagination.currentPage = pagination.currentPage;
          }

          if (pagination?.itemsPerPage) {
            this.pagination.itemsPerPage = pagination.itemsPerPage;
          }

          if (entries?.length) {
            entries.forEach((entry) => {
              this.entries.push({
                ...entry,
                page: pagination?.currentPage || 1,
              });
            });
          }

          if (!(pagination || entries)) {
            throw new Error('API failure during "courseFavourites/apiFetchEntries" request.');
          }

          return response;
        })
        .finally(() => {
          this.runningRequests.apiFetchEntries = false;
        });
    },

    /**
     * Adds or removes a course from the favourites list.
     */
    apiUpdateFavourite({ courseId, isFavorite }) {
      const sessionStore = useSessionStore();

      if (!sessionStore.getIsLoggedIn) {
        this._vm.$cdc.auth.openLogin();

        return Promise.reject("No user session while executing 'course/apiUpdateFavourite'.");
      }

      this.runningRequests.apiUpdateFavourite = true;

      const request = isFavorite ? this.apiAddFavourite(courseId) : this.apiRemoveFavourite(courseId);

      return request
        .then((response) => {
          const { data } = response.data || {};

          if (data) {
            this.favourites = data;

            return data;
          }

          throw new Error('Invalid response for "course/apiUpdateFavourite".');
        })
        .finally(() => {
          this.runningRequests.apiUpdateFavourite = false;
        });
    },

    /**
     * Adds a course to the favourites list.
     */
    apiAddFavourite(courseId: string) {
      const url = api.getUrl('course-favorite', { courseId });

      return api.post(url);
    },

    /**
     * Removes a course from the favourites list.
     */
    apiRemoveFavourite(courseId: string) {
      const url = api.getUrl('course-favorite', { courseId });

      return api.delete(url);
    },
  },
});
